import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'

import { env } from '@/lib/env'
import { logger, reportError } from '@/lib/logger'
import i18n from '@/lib/i18n/config'
import { acquireAccessToken, signIn } from '@/lib/auth/msal'
import { toast } from '@/stores/toast.store'
import { toApiError, type ApiProblem } from '@/api/api-error'

/** Per-request opt-out of the global toast, e.g. when a form renders its own errors. */
declare module 'axios' {
  export interface AxiosRequestConfig {
    skipErrorToast?: boolean
    skipAuth?: boolean
  }
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: env.api.baseUrl,
  timeout: env.api.timeoutMs,
  headers: { 'Content-Type': 'application/json' },
})

/* ------------------------------------------------------------------ */
/* Request: attach the Entra ID bearer token + current UI language      */
/* ------------------------------------------------------------------ */
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const backendLangKey = i18n.language === 'hu' ? 'hu-hu' : 'en-us'

    config.headers.set('LanguageKey', backendLangKey)

    if (config.skipAuth !== true) {
      const token = await acquireAccessToken()
      if (token) config.headers.set('Authorization', `Bearer ${token}`)
    }

    logger.debug(`[api] → ${config.method?.toUpperCase() ?? 'GET'} ${config.url ?? ''}`)
    return config
  },
  (error: unknown) => Promise.reject(toApiError(error)),
)

/* ------------------------------------------------------------------ */
/* Response: single place where 401 / 403 / 5xx are handled            */
/* ------------------------------------------------------------------ */

/** Avoids a redirect storm when several requests 401 at the same time. */
let reauthenticating = false

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiProblem>) => {
    const apiError = toApiError(error)

    if (apiError.code === 'CANCELED') {
      return Promise.reject(apiError)
    }

    const silent = error.config?.skipErrorToast === true

    switch (apiError.status) {
      case 401: {
        if (!reauthenticating) {
          reauthenticating = true
          logger.warn('[api] 401 — session expired, restarting sign-in.')
          if (!silent) toast.error(apiError.message)
          await signIn()
        }
        break
      }

      case 403: {
        logger.warn('[api] 403 — insufficient permissions', { url: error.config?.url })
        if (!silent) toast.error(apiError.message)
        break
      }

      case 0: {
        // Network failure or timeout.
        if (!silent) toast.error(apiError.message)
        break
      }

      default: {
        if (apiError.status >= 500) {
          reportError(apiError, { url: error.config?.url, traceId: apiError.traceId })
          if (!silent) {
            toast.error(
              apiError.message,
              apiError.traceId ? `Trace ID: ${apiError.traceId}` : undefined,
            )
          }
        } else if (!silent) {
          toast.error(apiError.message)
        }
        break
      }
    }

    return Promise.reject(apiError)
  },
)
